import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

type ToastTone = 'info' | 'success' | 'warning' | 'error';

interface Toast {
  id: number;
  text: string;
  tone: ToastTone;
}

interface ToastContextValue {
  show: (text: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (text: string, tone: ToastTone = 'info') => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, text, tone }]);
      // Auto-dismiss after 4s; user can also close manually via the X button
      setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 left-4 right-4 z-50 flex flex-col items-end gap-2 sm:left-auto"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto flex items-start gap-3 rounded-md pl-4 pr-2 py-2 text-sm shadow-md ring-1 max-w-sm ${classFor(
              t.tone,
            )}`}
          >
            <span className="min-w-0 flex-1 break-words pt-0.5">{t.text}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="-mr-1 rounded p-1 transition-opacity opacity-70 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be inside <ToastProvider>');
  return ctx;
}

function classFor(tone: ToastTone): string {
  switch (tone) {
    case 'success':
      return 'bg-green-50 text-green-800 ring-green-200';
    case 'warning':
      return 'bg-amber-50 text-amber-800 ring-amber-200';
    case 'error':
      return 'bg-red-50 text-red-800 ring-red-200';
    default:
      return 'bg-slate-900 text-white ring-slate-700';
  }
}

/**
 * Subscribe to global keyboard shortcuts. Returns a cleanup-aware hook
 * (no-op if `enabled` is false).
 */
export function useKeyboardShortcut(
  combo: string,
  handler: (e: KeyboardEvent) => void,
  opts: { enabled?: boolean; allowInInputs?: boolean } = {},
): void {
  const { enabled = true, allowInInputs = false } = opts;
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const target = e.target as HTMLElement | null;
      const inInput =
        target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (inInput && !allowInInputs) return;

      const parts = combo.toLowerCase().split('+');
      const wantsMod = parts.includes('mod') || parts.includes('cmd') || parts.includes('ctrl');
      const wantsShift = parts.includes('shift');
      const wantsAlt = parts.includes('alt');
      const key = parts[parts.length - 1];

      const modOk = wantsMod ? (isMac ? e.metaKey : e.ctrlKey) : !e.metaKey && !e.ctrlKey;
      const shiftOk = wantsShift ? e.shiftKey : !e.shiftKey;
      const altOk = wantsAlt ? e.altKey : !e.altKey;

      if (modOk && shiftOk && altOk && e.key.toLowerCase() === key) {
        e.preventDefault();
        handler(e);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combo, handler, enabled, allowInInputs]);
}
